// @flow

import React, { Fragment } from 'react';

import { Link } from '@zapier/design-system';

import help from 'app/get-help/components/blocks/Help';
import radio from 'app/get-help/components/blocks/Radio';
import { createDefaultContact } from 'app/get-help/content/nodes/loggedIn/zapTroubleshooting/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';


const defaultContact = createDefaultContact({
  message: {
    label: `Please describe the issue you're seeing and provide the name of the missing or blank field.`,
  },
  media: {
    label: `Please provide a full-screen screenshot of the most recent item where the field existed in the trigger app, but wasn't in the Zap editor or action app.`,
    details: `If there haven't been any items from the last 7 days, create a new record that has the field filled out and test your Zap. Then, provide the record you created as a screenshot by linking to or uploading it below. That way we can take a better look at where the problem might be occurring.`,
  },
});

const missing = radio({
  id: 'missing',
  label: 'Where are you seeing the problem?',
  zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.FIELDS_WHERE_IS_THE_PROBLEM,
  items: [
    {
      value: 'editor',
      label: 'Fields or options are missing in the Zap editor',
      zendeskCustomFieldValue: 'cf-fields-options-missing-editor',
      child: help({
        docs: {
          title: 'Check your app permissions or refresh fields',
          body: () => (
            <Fragment>
              <p>This might happen for one of several reasons:</p>
              <ol>
                <li>
                  You may not have permissions to edit those fields. Make sure
                  the account you've connected to Zapier has permissions to
                  access those fields within the app normally.
                </li>
                <li>
                  You may need to scroll to the bottom of the Zap editor and
                  click
                  <em>Refresh fields</em> to update the fields available and
                  bring new ones in.
                </li>
                <li>
                  There's a chance the integration or the app might not have
                  support for that field yet, especially if it's a custom field.
                  Let us know by writing in and we can add your vote for that
                  feature!
                </li>
              </ol>
            </Fragment>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title:
        //       'Using Extract Pattern to get phone numbers, emails and other types of text from a field.',
        //     href:
        //       'https://community.zapier.com/discussion/355/using-extract-pattern-to-get-phone-numbers-emails-and-other-types-of-text-from-a-field#latest',
        //     snippet:
        //       'A moderator explains how to get data from things like emails when it‘s not immediately available as an option.',
        //   },
        //   {
        //     title: 'Epic & attachments in JIRA Cloud Server',
        //     href:
        //       'https://community.zapier.com/discussion/552/epic-attachments-in-jira-cloud-server#latest',
        //     snippet:
        //       'Sometimes the answer is that an app can’t do what you want. A moderator explains why, and passes the Zap creator’s request to the app’s company.',
        //   },
        //   {
        //     title: 'Dynamic account lookup with Zapier',
        //     href:
        //       'https://community.zapier.com/discussion/473/dynamic-account-lookup-with-zapier#latest',
        //     snippet:
        //       'A Zapier Expert explains how to pass data from one Zap to another using Google Sheets.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
    {
      value: 'history',
      label: 'Data is not showing up when the Zap runs',
      zendeskCustomFieldValue: 'cf-data-not-showing-when-run',
      child: help({
        docs: {
          title: 'Check if the fields are mapped correctly in your action',
          body: () => (
            <Fragment>
              <p>This might happen for one of several reasons:</p>
              <ol>
                <li>
                  Your action step may need to be mapped with fields. When you
                  map a field, what you're doing is selecting a particular field
                  from the trigger and saying that the value for this field
                  should go into this other field in your action step. You can
                  learn{' '}
                  <Link href="/help/create/basics/set-up-your-zap-action#customize-your-options">
                    how to do that here.
                  </Link>
                </li>
                <li>
                  If you did have a mapping in that field, but it was blank when
                  it ran, make sure that the data was provided by the step it
                  was pulling from (usually the trigger). If that field wasn't
                  filled out or was blank in the previous step, that means it's
                  going to be blank when it's sent to the action. We recommend
                  making the field required if possible to make sure there's
                  always a value there. Or, you can try adding a step to
                  generate a default value as backup when the field is empty,{' '}
                  <Link href="/help/create/format/set-default-values-for-blank-fields-in-zaps">
                    following the instructions here.
                  </Link>
                </li>
              </ol>
            </Fragment>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title: 'Messenger to Gmail Not Pulling In Names',
        //     href:
        //       'https://community.zapier.com/discussion/535/messenger-to-gmail-not-pulling-in-names',
        //     snippet:
        //       'Some apps have privacy rules that cause Zaps to work in unexpected ways.',
        //   },
        //   {
        //     title:
        //       'Need some help with a Jira Zap - creating a Jira "issue" type, from google sheets',
        //     href:
        //       'https://community.zapier.com/discussion/425/need-some-help-with-a-jira-zap-creating-a-jira-issue-type-from-google-sheets',
        //     snippet:
        //       'A user’s data isn’t passing from Google Sheets into Jira, so a community member suggests a way to double check the data is formatted correctly.',
        //   },
        //   {
        //     title:
        //       'Things NOT to do on Google Sheets that are involved in live zaps',
        //     href:
        //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps#latest',
        //     snippet:
        //       'Sometimes, making changes to a Google Sheet associated with a live Zap can result in Zaps not passing data as expected.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
  ],
});

export default missing;
